import { type PanelRow } from '../../payload/panels'
import { usePeriod } from '../../payload/PeriodContext'
import { chartTheme } from '../charts/chartTheme'
import {
  BarsH,
  Card,
  CardHead,
  DataTable,
  Funnel,
  Grid,
  HelpMark,
  InlineBar,
  Note,
  SectionHead,
  Td,
  Tr,
} from '../components'
import { n, num, pct, plural, s } from '../format'
import { useBlank } from './useBlank'

const rateOf = (row: PanelRow): number | null =>
  row.rate === null || row.rate === undefined ? null : Number(row.rate)

/** Where people stop, and why. The pipeline itself is in Highlights. */
const Journey = () => {
  const { one, panel } = usePeriod()
  const blank = useBlank()
  const furthest = panel('furthest_step')
  const act = panel('activation')
  const drop = panel('dropoff')
  const ret = panel('retention')
  const o = one('overview')

  const failures = drop.filter(d => d.kind === 'failure')
  const transitions = drop.filter(d => d.kind === 'transition')
  const failMax = Math.max(1, ...failures.map(f => n(f.people)))

  const worstFail = failures.reduce<(typeof failures)[number] | null>(
    (worst, f) => (worst === null || n(f.people) > n(worst.people) ? f : worst),
    null
  )
  const worstStep = transitions
    .filter(t => rateOf(t) !== null && (rateOf(t) as number) <= 1)
    .reduce<
      (typeof transitions)[number] | null
    >((worst, t) => (worst === null || (rateOf(t) as number) < (rateOf(worst) as number) ? t : worst), null)

  return (
    <section className="grid gap-4">
      <SectionHead
        title="Journey"
        question="Where people stop, and why. The pipeline itself is in Highlights; this is the detail behind its worst steps."
      />

      <Grid cols={2}>
        <Card>
          <CardHead
            title="Where visitors stop"
            info={[
              'Each visitor counted once, at the furthest point they got to. Unlike the reach bars above, these buckets are mutually exclusive and do add up — so a long bar here is a genuine drop-off.',
              '"Arrived, but matched no step" is activity that hit no funnel step at all: app-only sessions from before the website was tracked, and support-widget traffic. A growing bucket means the funnel definition has fallen behind the product.',
            ]}
          />
          {furthest.length ? (
            <BarsH
              rows={furthest}
              labelKey="step"
              label="Where visitors stop"
              labelWidth={200}
              series={[
                {
                  key: 'visitors',
                  label: 'Visitors',
                  color: chartTheme.series[0],
                },
              ]}
            />
          ) : (
            blank('furthest_step', 'Nobody started the journey in this period.')
          )}
        </Card>

        <Card>
          <CardHead
            title="From signup to a usable plan"
            info={[
              'Measured in the app database rather than in activity, because past the sign-in boundary the database is complete and the event stream is not.',
              'Each step is a strict subset of the one above, so these percentages are genuine drop-offs. Team and test accounts are shown alongside, never added in.',
              'Depth only. Recency is a different axis and is not a step here — an active account need not have covered six topics.',
              // One paragraph per step rather than one semicolon-joined run-on.
              ...act
                .filter(r => r.means)
                .map(r => `${s(r.step)} — ${s(r.means)}.`),
            ]}
          />
          {act.length ? (
            <Funnel
              rows={act}
              valueKey="real_users"
              labelKey="step"
              compare={{ key: 'team_users', label: 'team & test accounts' }}
            />
          ) : (
            blank('activation', 'No accounts were active in this period.')
          )}
        </Card>
      </Grid>

      <Grid cols={2}>
        <Card>
          <CardHead
            title="Where the product breaks"
            info={[
              'Times the app reported that something had gone wrong. These are the drop-off causes we can name, and therefore fix — as opposed to the step-to-step rates, which locate a loss without explaining it.',
            ]}
          />
          {failures.length ? (
            <DataTable
              label="Where the product breaks"
              head={[
                { label: 'What went wrong' },
                { label: '' },
                { label: 'Visitors', num: true },
                { label: 'Times', num: true },
              ]}
            >
              {failures.map(f => (
                <Tr key={s(f.label)}>
                  <Td>
                    {s(f.label)}
                    <HelpMark text={`Reported by ${s(f.area)}.`} />
                  </Td>
                  <Td>
                    <InlineBar
                      value={n(f.people)}
                      max={failMax}
                      hint={`${plural(n(f.people), 'visitor')} · ${plural(n(f.occurrences), 'time')}`}
                    />
                  </Td>
                  <Td num>{num(f.people)}</Td>
                  <Td num muted>
                    {num(f.occurrences)}
                  </Td>
                </Tr>
              ))}
            </DataTable>
          ) : (
            blank(
              'dropoff',
              'Nothing broke in this period — no errors were reported.'
            )
          )}
          <Note
            flag
            parts={
              worstFail && [
                <strong key="lead">
                  Our biggest named loss is in {s(worstFail.area)}.{' '}
                </strong>,
                `${plural(n(worstFail.people), 'visitor')} hit "${s(worstFail.label)}", `,
                n(worstFail.occurrences) > n(worstFail.people)
                  ? `${plural(n(worstFail.occurrences), 'time')} in total. `
                  : '',
                'That is something broken rather than people losing interest, which makes it the most actionable line on this page.',
              ]
            }
          />
        </Card>

        <Card>
          <CardHead
            title="Drop-off at each step"
            info={[
              'The share of visitors who made it from one step to the next, in funnel order.',
              'A rate above 100% is not an error: visitors who sign up on the website skip the previous step entirely, so more can finish a step than started the one before it.',
            ]}
          />
          {transitions.length ? (
            <DataTable
              label="Drop-off at each step"
              head={[
                { label: 'Step' },
                { label: '' },
                { label: 'Through', num: true },
                { label: 'Of', num: true },
              ]}
            >
              {transitions.map(t => {
                const rate = rateOf(t)
                return (
                  <Tr key={s(t.label)}>
                    <Td>{s(t.label)}</Td>
                    <Td>
                      <InlineBar
                        value={Math.min(rate ?? 0, 1)}
                        max={1}
                        alt={(rate ?? 0) < 0.5}
                        hint={
                          rate === null
                            ? 'no data'
                            : `${Math.round(rate * 100)}% of ${num(t.occurrences)}`
                        }
                      />
                    </Td>
                    <Td num nowrap>
                      {rate === null ? '—' : `${Math.round(rate * 100)}%`}
                      {(rate ?? 0) > 1 && (
                        <HelpMark text="Over 100%: visitors who sign up on the website skip the previous step entirely, so more can finish this step than started the one before it." />
                      )}
                    </Td>
                    <Td num muted>
                      {num(t.occurrences)}
                    </Td>
                  </Tr>
                )
              })}
            </DataTable>
          ) : null}
          <Note
            parts={
              worstStep && [
                <strong key="lead">The weakest step is </strong>,
                <strong key="name">{`"${s(worstStep.label)}"`}</strong>,
                `, at ${Math.round((rateOf(worstStep) ?? 0) * 100)}%.`,
              ]
            }
          />
        </Card>
      </Grid>

      <Card>
        <CardHead
          title="Do they come back?"
          info={[
            'Three groups, because the answer differs by who is counted and by which system can see them. Real users and team accounts come from the app database; visitors come from activity and include anonymous ones, so that row is "did this browser return", not "did this person return".',
            n(o.real_with_any_data) > 0 &&
              `Only ${plural(n(o.real_with_any_data), 'real user')} saved anything in this period, so each one moves the real-user share by about ${Math.round(100 / n(o.real_with_any_data))} points. Read the counts, not the rates.`,
            'No cohort-by-week grid: the person export has no update timestamp, so every person trend would be a cohort of creation with today’s values pinned to historical weeks.',
          ]}
        />
        {ret.length ? (
          <DataTable
            label="Do they come back?"
            head={[
              { label: 'What they did' },
              { label: 'Who' },
              { label: 'How many', num: true },
              { label: 'Share', num: true },
            ]}
          >
            {ret.map((r, i) => (
              <Tr key={i}>
                <Td>{s(r.behaviour)}</Td>
                <Td muted>{s(r.cohort)}</Td>
                <Td num nowrap>
                  <strong>{num(r.people)}</strong>
                  <span className="text-muted">{` of ${num(r.out_of)}`}</span>
                  <HelpMark
                    text={`Out of ${num(r.out_of)} ${s(r.out_of_means)}.`}
                  />
                </Td>
                <Td num>{pct(n(r.people), n(r.out_of))}</Td>
              </Tr>
            ))}
          </DataTable>
        ) : (
          blank(
            'retention',
            'Nobody was active in this period, so there is nothing to return from.'
          )
        )}
      </Card>
    </section>
  )
}

export default Journey
